/**
 * The Transaction Audit filter vocabulary and the mapping between what the
 * "Filter by" dropdown displays and what the audit endpoint expects.
 *
 * Saved views persist only the API values; the dropdown's labels are derived from
 * them on the way back in (`deriveFilterLabels`). Storing both used to let the two
 * drift, so a view could show "All Statuses" while the request still filtered on
 * one. That makes `toApiFilterValue` and `toDisplayLabel` exact inverses over
 * every option below — see transactionFilters.test.js.
 */

export const FILE_TYPE_OPTIONS = [
    "All File Types",
    "850 New", "850 Update", "Cancel Order", "Status Update (In)",
    "990", "214", "210",
    "204 Tender", "204 Update", "204 Recall", "204 Cancel",
    "204 Stop Reorder", "204 Charge Update",
    "211 New", "211 Update", "Status Update (Out)",
    "Invoice", "997 ACK 990", "997 ACK 214", "997 ACK 210",
];

export const FILE_TYPE_MAP = {
    "All File Types": "",
    "850 New": "EDI_850_NEW",
    "850 Update": "EDI_850_UPDATE",
    "Cancel Order": "EDI_CANCEL_ORDER",
    "Status Update (In)": "EDI_STATUS_UPDATE_INBOUND",
    990: "EDI_990",
    214: "EDI_214",
    210: "EDI_210",
    "204 Tender": "EDI_204_TENDER",
    "204 Update": "EDI_204_UPDATE",
    "204 Recall": "EDI_204_RECALL",
    "204 Cancel": "EDI_204_CANCEL",
    "204 Stop Reorder": "EDI_204_STOP_REORDER",
    "204 Charge Update": "EDI_204_CHARGE_UPDATE",
    "211 New": "EDI_211_NEW",
    "211 Update": "EDI_211_UPDATE",
    "Status Update (Out)": "EDI_STATUS_UPDATE_OUTBOUND",
    "Invoice": "EDI_INVOICE",
    "997 ACK 990": "EDI_997_ACK_990",
    "997 ACK 214": "EDI_997_ACK_214",
    "997 ACK 210": "EDI_997_ACK_210",
};

export const SOURCE_MAP = {
    "All Sources": "",
    "IFS":         "IFS",
    "Carrier EDI": "CARRIER_EDI",
    "Ascend":      "ASCEND",
};

export const FLOW_OPTIONS   = ["All Flows",     "Inbound",   "Outbound"];
export const STATUS_OPTIONS = ["All Statuses",  "Success","Failure"];
export const REF_OPTIONS    = ["All Ref Types", "ORDER",     "SHIPMENT", "INVOICE"];
export const SOURCE_OPTIONS = ["All Sources", "IFS", "Carrier EDI", "Ascend"];

export const DEFAULT_FILTER_VALUES = {
    fileType:         "",
    flowType:         "",
    referenceType:    "",
    processingStatus: "",
    source:           "",
};

export const DEFAULT_FILTER_LABELS = {
    fileTypeLabel: "All File Types",
    flowTypeLabel: "All Flows",
    refTypeLabel:  "All Ref Types",
    statusLabel:   "All Statuses",
    sourceLabel:   "All Sources",
};

/** Staged selections inside the "Filter by" dropdown, before Apply Filters. */
export const DEFAULT_DRAFT_FILTERS = { ...DEFAULT_FILTER_LABELS, keyword: "" };

/** Option list per draft key, handed to the dropdown's sections. */
export const FILTER_OPTIONS = {
    fileTypeLabel: FILE_TYPE_OPTIONS,
    flowTypeLabel: FLOW_OPTIONS,
    refTypeLabel:  REF_OPTIONS,
    sourceLabel:   SOURCE_OPTIONS,
    statusLabel:   STATUS_OPTIONS,
};

/** Display label → the value the audit endpoint expects for that filter. */
export const toApiFilterValue = (colKey, displayVal = "") => {
    const isAll = displayVal.startsWith("All");
    if (colKey === "fileType") return FILE_TYPE_MAP[displayVal] ?? "";
    if (colKey === "source")   return SOURCE_MAP[displayVal] ?? "";
    if (colKey === "flowType" || colKey === "processingStatus") {
        if (isAll)                    return "";
        if (displayVal === "Success") return "SUCCESSFUL";
        if (displayVal === "Failure") return "UNSUCCESSFUL";
        return displayVal.toUpperCase();
    }
    // referenceType — ORDER, SHIPMENT, INVOICE already uppercase
    return isAll ? "" : displayVal;
};

/** value → label, so a saved view's stored filter values can rebuild their labels. */
const invertMap = (map) =>
    Object.fromEntries(Object.entries(map).map(([label, value]) => [value, label]));

const FILE_TYPE_LABELS = invertMap(FILE_TYPE_MAP);
const SOURCE_LABELS    = invertMap(SOURCE_MAP);

/** Inverse of `toApiFilterValue` — the dropdown label for a stored API value. */
export const toDisplayLabel = (colKey, apiVal = "") => {
    switch (colKey) {
        case "fileType":
            return FILE_TYPE_LABELS[apiVal] ?? DEFAULT_FILTER_LABELS.fileTypeLabel;
        case "source":
            return SOURCE_LABELS[apiVal] ?? DEFAULT_FILTER_LABELS.sourceLabel;
        case "flowType":
            if (!apiVal) return DEFAULT_FILTER_LABELS.flowTypeLabel;
            return apiVal === "INBOUND" ? "Inbound" : "Outbound";
        case "processingStatus":
            if (!apiVal) return DEFAULT_FILTER_LABELS.statusLabel;
            return apiVal === "SUCCESSFUL" ? "Success" : "Failure";
        default:
            // referenceType — ORDER / SHIPMENT / INVOICE round-trip unchanged
            return apiVal || DEFAULT_FILTER_LABELS.refTypeLabel;
    }
};

/** Labels for a whole filter set, derived so they can't drift from the values. */
export const deriveFilterLabels = (values = {}) => ({
    fileTypeLabel: toDisplayLabel("fileType",         values.fileType),
    flowTypeLabel: toDisplayLabel("flowType",         values.flowType),
    refTypeLabel:  toDisplayLabel("referenceType",    values.referenceType),
    statusLabel:   toDisplayLabel("processingStatus", values.processingStatus),
    sourceLabel:   toDisplayLabel("source",           values.source),
});

// ─── Row display ──────────────────────────────────────────────────────────────

/** Friendly badge text per known enum value, for the table body. */
export const FLOW_ROW_LABELS = {
    INBOUND:  "Inbound",
    OUTBOUND: "Outbound",
};

export const STATUS_ROW_LABELS = {
    SUCCESSFUL:   "Success",
    UNSUCCESSFUL: "Failure",
};

export const SOURCE_ROW_LABELS = {
    IFS:         "IFS",
    CARRIER_EDI: "Carrier EDI",
    ASCEND:      "Ascend",
};

/**
 * Badge text for an enum coming off the audit endpoint.
 *
 * Falls through to the raw value for anything unrecognised rather than assuming one
 * of the known labels. The row used to render `status === "SUCCESSFUL" ? … :
 * "Unsuccessful"`, so a backend that renamed the enum would report every single row
 * as a failure and look entirely plausible while doing it. An unknown value has to
 * be visible for a mismatch to be diagnosable.
 */
export const formatEnumValue = (value, labels = {}) => {
    if (value === null || value === undefined || value === "") return "—";
    return labels[value] ?? String(value);
};

/** The filter values a set of dropdown labels resolves to. */
export const toApiFilterValues = (labels = {}) => ({
    fileType:         toApiFilterValue("fileType",         labels.fileTypeLabel ?? ""),
    flowType:         toApiFilterValue("flowType",         labels.flowTypeLabel ?? ""),
    referenceType:    toApiFilterValue("referenceType",    labels.refTypeLabel  ?? ""),
    processingStatus: toApiFilterValue("processingStatus", labels.statusLabel   ?? ""),
    source:           toApiFilterValue("source",           labels.sourceLabel   ?? ""),
});
